import styled from 'styled-components';

import { ANIM, COLORS, FEATURE_TONE_COLORS, VS_BAR_COLORS, VS_BAR_PACK } from '../../theme/tokens';
import { Text } from '../Text/Text';

/** Bar height = the tier pill (21px) + a 1px fill frame top and bottom, so the pill nests INSIDE
 *  the bar. This is the configurator find-my-pack DetailTrack geometry, which the product feature
 *  detail (pages/product-detail/components/BenchmarkBar) already follows. */
const TRACK_H = 23;

/** Rounded end-cap radius (half the bar height). An end-cap marker points at the CENTRE of its
 *  fill's cap, not at its raw %, so the arrow sits a touch inside the leading edge. A pill-anchored
 *  marker takes no such nudge — there is no cap to aim at (see the Marker below). */
export const MARKER_CAP_PX = TRACK_H / 2;
const CAP = MARKER_CAP_PX;

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const Label = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textSubtle};
`;

/** Holds the track plus the absolutely-positioned You/Market markers below it. */
export const TrackArea = styled.div`
  position: relative;
  padding-bottom: 26px;
`;

export const Track = styled.div`
  position: relative;
  /* Own stacking context so the market hatch / You fill / tier pill z-order resolves HERE (the
     pill must always paint above the hatch). It also contains those z-indexes inside the track:
     without it they resolve at the nearest ancestor stacking context and tie with the PageShell
     HeaderScrim (also z-index:1), so the bar painted OVER the scrim and showed through the header
     as the card scrolled up (the rest of the card, having no z-index, stayed hidden). */
  isolation: isolate;
  height: ${TRACK_H}px;
  border-radius: 12px;
  /* The studio's own opaque track (NOT the configurator's translucent white-5% fpTrackBg): on this
     area's light card surface a 5% white track is invisible, and most rows here are inactive — an
     invisible track would read as a missing bar rather than as an empty one. */
  background: ${VS_BAR_COLORS.track};
`;

/** Market "range" segment (0 → market%). When $onTop (You ≥ Market) it overlays the You fill as a
 *  WHITE hatch so the stripes read over the fill; otherwise it sits BEHIND the fill as a grey hatch
 *  that shows only in the You→Market gap. Either way the tier pill (z-index 10) rides above it. */
export const MarketFill = styled.div<{ $w: number; $delay: number; $onTop: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: ${({ $onTop }): number => ($onTop ? 3 : 1)};
  width: ${({ $w }): string => `${Math.min(100, Math.max(0, $w))}%`};
  border-radius: 12px;
  background: ${({ $onTop }): string => ($onTop ? VS_BAR_PACK.marketHashOver : VS_BAR_PACK.marketHash)};
  transition: width ${ANIM.BAR_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }): string => `${$delay}ms`};
`;

/** "You" fill (0 → value%): the find-my-pack deep-blue→periwinkle gradient. Uniform across rows —
 *  the feature's status is carried by its status line, not by the bar's colour. */
export const YouFill = styled.div<{ $w: number; $delay: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  width: ${({ $w }): string => `${Math.min(100, Math.max(0, $w))}%`};
  border-radius: 12px;
  background: ${VS_BAR_PACK.youFill};
  transition: width ${ANIM.BAR_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }): string => `${$delay}ms`};
`;

/** "You" at 0, with no pill to state it: a round dot (diameter = the track height) drawn instead of
 *  a zero-length fill, so the row still marks where it stands. When there IS a pill (the "Off"
 *  state), the pill takes this spot instead. The dot and the market hatch share the track's left:0
 *  origin, so a ring in the track's own colour cuts a visible gap between them — without it the two
 *  ran together and read as one fused pill instead of a distinct "You" marker. */
export const Dot = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
  width: ${TRACK_H}px;
  box-sizing: border-box;
  border: 2px solid ${VS_BAR_COLORS.track};
  border-radius: 50%;
  background-color: ${VS_BAR_PACK.youDot};
`;

/**
 * One flex row across the WHOLE track, holding the tier pill (the same structure the product-detail
 * BenchmarkBar uses, so the two bars place their pill by one rule). The pill is px-sized while every
 * position here is a PERCENTAGE, and each attempt to reconcile the two with a threshold put the pill
 * somewhere it must not be. Flex layout settles it structurally: max-width: 100% on the pill is
 * measured against this row, i.e. the track, so no label can take it outside the bar, and the pill's
 * own margins do the positioning (see TierPill).
 */
export const PillSlot = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
`;

/** The pill itself (mirrors the configurator DetailTierPill). $off paints it in the inactive red
 *  (with the status line's own red, so the two read as one statement) instead of the neutral tier
 *  surface.
 *
 *  margin-left: auto pins the pill's RIGHT edge to the fill's tip — ON the fill, riding the tip,
 *  the find-my-pack vocabulary this bar speaks. The tip itself is held by the PillGap after it, so
 *  where the fill is too short to hold the label the gap collapses and the pill keeps its natural
 *  width, extending RIGHTWARDS into empty track. A tier label is API data of unbounded length
 *  ("META JACKPOT - PLUS"), so max-width bounds it to the track, where it ellipsizes as the last
 *  resort. Swept in the CT spec across track widths, tiers and label lengths. */
export const TierPill = styled.span<{ $off: boolean }>`
  box-sizing: border-box;
  /* NEVER shrinks. Flex distributes shrinkage across every shrinkable item in proportion to
     base x factor, so even a heavily weighted gap left the pill a fraction of a pixel short — and a
     fraction is all it takes for text-overflow to eat a character ("Off" rendered "O..." on a
     180px card). The gap alone yields; the bound below is what stops the pill instead. */
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
  margin-left: auto;
  overflow: hidden;
  border-radius: 12px;
  background: ${({ $off }): string => ($off ? FEATURE_TONE_COLORS.inactive : VS_BAR_PACK.tierBg)};
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 700;
  line-height: 11px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${({ $off }): string => ($off ? COLORS.onAccent : VS_BAR_PACK.tierAccent)};
`;

/** The empty track between the fill's tip and the track's end. Sizing this instead of positioning
 *  the pill is what makes the row's priorities explicit: the gap gives up its space first, so a pill
 *  too wide for its fill grows RIGHTWARDS into empty track rather than being squeezed. Its basis is
 *  what animates, so the pill rides the fill it labels. */
export const PillGap = styled.span<{ $at: number; $delay: number }>`
  /* The ONLY shrinkable item in the row, so every pixel the pill needs beyond its fill comes out
     of here — the pill keeps its natural width until the gap is gone, and only the pill's own
     max-width stops it after that. */
  flex: 0 1 ${({ $at }): string => `${100 - Math.min(100, Math.max(0, $at))}%`};
  min-width: 0;
  transition: flex-basis ${ANIM.BAR_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  /* The same stagger the fill takes (see YouFill), or on the 2nd+ card the pill starts moving a
     whole delay ahead of its own fill and detaches from the tip on the way in. */
  transition-delay: ${({ $delay }): string => `${$delay}ms`};
`;

export type MarkerAlign = 'center' | 'start' | 'end';

const FLEX_ALIGN: Record<MarkerAlign, string> = {
  center: 'center',
  start: 'flex-start',
  end: 'flex-end',
};

/** The arrow points at its fill's rounded end-cap centre, so the whole column nudges left by CAP;
 *  end right-aligns the column (it runs left), start left-aligns it (runs right), center
 *  centres it — so an edge marker's label is never clipped by the bar's edge. */
const markerShift = (align: MarkerAlign): string => {
  if (align === 'end') return `translateX(calc(-100% - ${CAP}px))`;
  if (align === 'start') return `translateX(-${CAP}px)`;
  return `translateX(calc(-50% - ${CAP}px))`;
};

/** Half the arrow's own width (MarkerArrow is 12px wide). start left-aligns the column, so the
 *  arrow's CENTRE sits ARROW_HALF px right of the column's left edge, not ON it — needed to line
 *  the arrow up with the dot below it. */
const ARROW_HALF = 6;

/** The CAP-px left nudge (in markerShift) assumes there is CAP px of track to its left to nudge
 *  INTO (true for any real fill's right-edge cap). Right at the track's own ends (You at 0, market
 *  at 100) there is no such room, so the raw anchor also has to stay at least CAP px in from that
 *  edge — otherwise the nudge pushes the whole column past the bar and the label is clipped by
 *  whatever sits beside the bar. start's floor is raised a further ARROW_HALF px so the arrow's
 *  centre (not its edge) lands exactly CAP px in — matching where the "You at 0" dot sits. */
const markerLeft = ($left: number, align: MarkerAlign): string => {
  const pct = `${Math.min(100, Math.max(0, $left))}%`;
  if (align === 'start') return `max(${pct}, ${2 * CAP - ARROW_HALF}px)`;
  if (align === 'end') return `min(${pct}, calc(100% - ${CAP}px))`;
  return pct;
};

/** Which edge of a pill-anchored column lands on the anchor. The arrow must stay ON the pill's
 *  centre, so the CAP nudge is dropped (there is no end-cap to aim at) — but the ALIGNMENT still
 *  matters: near the track's ends the column has to run inward, or the label hangs off the bar
 *  (measured: a centred "You and Market" at value 0 painted 21px into the card's name column).
 *  align-items puts the arrow on that same edge, so the arrow stays put while the label swings. */
/** A pill-anchored marker keeps its ARROW on the pill and lets the LABEL slide when the two cannot
 *  both fit near a track end (labelClampPx) — which is why this mode centres unconditionally
 *  rather than switching alignment the way an end-cap marker does: the column is centred on the
 *  anchor, and only the label moves. position: relative so its shift leaves the arrow alone. */
export const MarkerLabelBox = styled.span<{ $shift: number }>`
  position: relative;
  /* Deliberately NOT transitioned. Animating this looked like the obvious way to stop the label
     jumping to its settled correction mid-entrance, and measured the opposite: A/B over 24
     configurations improved none and made 12 worse. The marker's own transform snaps when the first
     measurement flips it onto the pill, so an animated shift starts the label a full correction
     away from where the un-animated one starts and crawls back — 22px outside the track for ~730ms
     on the inactive-feature card, against 1px when it simply follows. */
  transform: ${({ $shift }): string => `translateX(${$shift}px)`};
`;

/** One marker: its ▲ arrow stacked over its label in a single column, just below the track.
 *
 *  $onPill anchors the column on the TIER PILL's centre instead of on a fill's end-cap (the
 *  "You" marker, which names the same thing the pill does): no cap nudge, and no edge clamp on the
 *  anchor — the pill's centre is inside the track by construction, and onPillShift keeps the
 *  label inside too. */
export const Marker = styled.div<{ $left: number; $align: MarkerAlign; $delay: number; $onPill?: boolean }>`
  position: absolute;
  top: ${TRACK_H + 4}px;
  left: ${({ $left, $align, $onPill }): string =>
    $onPill ? `${Math.min(100, Math.max(0, $left))}%` : markerLeft($left, $align)};
  transform: ${({ $align, $onPill }): string => ($onPill ? 'translateX(-50%)' : markerShift($align))};
  display: flex;
  flex-direction: column;
  align-items: ${({ $align, $onPill }): string => ($onPill ? 'center' : FLEX_ALIGN[$align])};
  gap: 2px;
  transition: left ${ANIM.BAR_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${({ $delay }): string => `${$delay}ms`};
`;

/** Rounded-corner up-arrow (a sharp CSS border-triangle can't be rounded): fill + stroke share the
 *  marker colour and stroke-linejoin:round softens the three corners.
 *  Render as <MarkerArrow viewBox="0 0 12 8"><path d="M6 1.5L10.5 6.5L1.5 6.5Z" /></MarkerArrow>. */
export const MarkerArrow = styled.svg`
  display: block;
  width: 12px;
  height: 8px;
  fill: ${VS_BAR_COLORS.marker};
  stroke: ${VS_BAR_COLORS.marker};
  stroke-width: 3;
  stroke-linejoin: round;
`;

export const MarkerLabel = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${VS_BAR_COLORS.marker};
`;
